import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Building2 } from 'lucide-react'
import './AccountWorkspace.css'

import { useStore, useHasPermission } from '@/store/useStore'
import { RecordHero } from '@/components/workspace/RecordHero'
import { Badge, HealthBadge, EmptyState, Button } from '@/components/ui'
import { Tabs, type TabDef } from '@/components/ui'
import { QuickActionBar, toActionContacts } from '@/components/ui'
import {
  accountById, tracksForAccount, propertiesForAccount, contactsForAccount,
  allContactsForAccount, isInternalAccount,
  opportunitiesForAccount, openItemsForAccount, financialPeriodsForProperty,
  activitiesForRef, workOrdersForProperty, noi, latest,
} from '@/lib/selectors'
import { ACCOUNT_STATUS_OPTIONS, type AccountStatus, type FinancialPeriod } from '@/types/entities'
import { money, num } from '@/lib/format'
import {
  sumLatestRevenue, sumNoiActual, acctTypeTone,
} from './lib'

import OverviewTab from './tabs/OverviewTab'
import InsightsTab from './tabs/InsightsTab'
import ContactsTab from './tabs/ContactsTab'
import PropertiesTab from './tabs/PropertiesTab'
import VitalFactorsTab from './tabs/VitalFactorsTab'
import FinancialsTab from './tabs/FinancialsTab'
import DealsTab from './tabs/DealsTab'
import ScheduledTab from './tabs/ScheduledTab'
import ActionItemsTab from './tabs/ActionItemsTab'
import WorkOrdersTab from './tabs/WorkOrdersTab'
import ProjectsTab from './tabs/ProjectsTab'
import HistoryTab from './tabs/HistoryTab'
import { GenericEmptyTab } from './tabs/GenericEmptyTab'
import { SyncEmailModal } from './SyncEmailModal'
import { AccountForm } from './AccountForm'

const TAB_ORDER = [
  'overview', 'insights', 'contacts', 'properties', 'vital-factors',
  'financials', 'deals', 'diligence', 'scheduled', 'action-items',
  'work-orders', 'projects', 'history', 'files',
] as const

export default function AccountWorkspace() {
  const { accountId, tab } = useParams()
  const navigate = useNavigate()
  const data = useStore((s) => s.data)
  const patchStore = useStore((s) => s.patch)
  const canManage = useHasPermission('crm.manage')
  const [emailSyncOpen, setEmailSyncOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)

  const account = accountId ? accountById(data, accountId) : undefined

  const derived = useMemo(() => {
    if (!account) return null
    const tracks = tracksForAccount(data, account.id)
    const properties = propertiesForAccount(data, account.id)
    // Staff are excluded from a client account's contacts (see the selector);
    // the internal account is the one page where they belong.
    const contacts = isInternalAccount(account)
      ? allContactsForAccount(data, account.id)
      : contactsForAccount(data, account.id)
    const opportunities = opportunitiesForAccount(data, account.id)
    const openItems = openItemsForAccount(data, account.id)

    const propIds = new Set(properties.map((p) => p.id))
    const events = data.events.filter((e) => {
      const r = e.attachedTo
      if (!r) return false
      if (r.kind === 'account' && r.id === account.id) return true
      if (r.kind === 'track' && tracks.some((t) => t.id === r.id)) return true
      if (r.kind === 'property' && propIds.has(r.id)) return true
      return false
    })
    const projects = data.projects.filter((pr) => {
      const r = pr.attachedTo
      const hasProp = pr.propertyIds?.some((id) => propIds.has(id))
      if (hasProp) return true
      if (!r) return false
      if (r.kind === 'account' && r.id === account.id) return true
      if (r.kind === 'track' && tracks.some((t) => t.id === r.id)) return true
      if (r.kind === 'property' && propIds.has(r.id)) return true
      return false
    })
    const workOrders = properties.flatMap((p) => workOrdersForProperty(data, p.id))
    const financialPeriods = properties.flatMap((p) => financialPeriodsForProperty(data, p.id))

    // Activities roll up from account + tracks + properties + contacts.
    const actSet = new Map<string, (typeof data.activities)[number]>()
    for (const a of activitiesForRef(data, 'account', account.id)) actSet.set(a.id, a)
    for (const t of tracks) for (const a of activitiesForRef(data, 'track', t.id)) actSet.set(a.id, a)
    for (const p of properties) for (const a of activitiesForRef(data, 'property', p.id)) actSet.set(a.id, a)
    for (const c of contacts) for (const a of activitiesForRef(data, 'contact', c.id)) actSet.set(a.id, a)
    const activities = [...actSet.values()]

    const insight = data.accountInsights.find((ai) => ai.accountId === account.id)

    const revenue = sumLatestRevenue(properties)
    const noiActual = sumNoiActual(data, properties)
    const stabilized = properties.filter((p) => p.stabilization === 'Stabilized').length

    return {
      tracks, properties, contacts, opportunities, openItems, propIds,
      events, projects, workOrders, financialPeriods, activities, insight,
      revenue, noiActual, stabilized,
    }
  }, [account, data])

  if (!account || !derived) {
    return (
      <div className="card">
        <EmptyState
          icon={<Building2 size={40} />}
          title="Account not found"
          hint={<>No account matches <b>{accountId}</b>.</>}
          action={<Button variant="default" onClick={() => navigate('/accounts')}>Back to Accounts</Button>}
        />
      </div>
    )
  }

  const activeTab = (tab && (TAB_ORDER as readonly string[]).includes(tab) ? tab : 'overview')
  const goTab = (id: string) => navigate(`/accounts/${account.id}/${id === 'overview' ? '' : id}`)

  const trackSummary = derived.tracks.length === 2
    ? `${derived.tracks.length} competing tracks`
    : derived.tracks.length === 1
      ? derived.tracks[0].name
      : derived.tracks.length > 0
        ? `${derived.tracks.length} tracks`
        : 'No active tracks'

  const tel = account.phone?.replace(/[^+\d]/g, '')

  const tabs: TabDef[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'insights', label: 'Insights' },
    { id: 'contacts', label: 'Contacts', count: derived.contacts.length || undefined },
    { id: 'properties', label: 'Properties', count: derived.properties.length || undefined },
    { id: 'vital-factors', label: 'Vital Factors' },
    { id: 'financials', label: 'Financials', count: derived.financialPeriods.length || undefined },
    { id: 'deals', label: 'Deals', count: derived.opportunities.length || undefined },
    { id: 'diligence', label: 'Diligence' },
    { id: 'scheduled', label: 'Scheduled', count: derived.events.length || undefined },
    { id: 'action-items', label: 'Action Items', count: derived.openItems.length || undefined },
    { id: 'work-orders', label: 'Work Orders', count: derived.workOrders.length || undefined },
    { id: 'projects', label: 'Projects', count: derived.projects.length || undefined },
    { id: 'history', label: 'History', count: derived.activities.length || undefined },
    { id: 'files', label: 'Files' },
  ]

  // NOI as a monthly run-rate so it sits on the same time basis as In-Place Revenue (/mo).
  // Each financial period's actual NOI is divided by the months it covers
  // (quarter=3, month=1, T12=12), then summed across all of the account's periods.
  // Prefer the live per-property NOI snapshots (synced Yardi statements,
  // latest complete month) — same basis as In-Place Revenue. Seed financial
  // periods are only a fallback for accounts with no synced statements.
  const MONTHS_IN_GRAIN: Record<FinancialPeriod['grain'], number> = { month: 1, quarter: 3, T12: 12 }
  const liveNoi = derived.properties
    .map((p) => latest(p.noi))
    .filter((v): v is number => v != null)
  let noiMonthly: number | undefined = liveNoi.length
    ? liveNoi.reduce((s, v) => s + v, 0)
    : undefined
  if (noiMonthly == null && derived.financialPeriods.length) {
    noiMonthly = derived.financialPeriods.reduce(
      (sum, fp) => sum + noi(fp, 'actual') / MONTHS_IN_GRAIN[fp.grain],
      0,
    )
  }

  const heroKpis = [
    { label: 'In-Place Revenue', value: money(derived.revenue, { compact: true }), unit: '/mo' },
    { label: 'NOI', value: noiMonthly != null ? money(noiMonthly, { compact: true }) : '—', unit: '/mo' },
    { label: 'Properties', value: num(derived.properties.length) },
    { label: 'Stabilized', value: num(derived.stabilized) },
    { label: 'Open Items', value: num(derived.openItems.length) },
  ]

  return (
    <div className="aw">
      <RecordHero
        tone="navy"
        breadcrumb={<><Link to="/accounts">Accounts</Link><span className="aw-bc-sep">/</span><span>{account.name}</span></>}
        eyebrow={<>{account.type} · {trackSummary}</>}
        title={account.name}
        badges={
          <>
            <Badge tone={acctTypeTone(account.type)} variant="solid">{account.type}</Badge>
            {/* Status is editable in place — before this there was NO surface
                to change an account's status after creation (Jason 7/28). */}
            <select
              className="aw-status-select"
              value={account.status}
              onChange={(e) => patchStore('accounts', account.id, { status: e.target.value as AccountStatus })}
              title="Account status"
            >
              {ACCOUNT_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {account.metrics?.health && <HealthBadge health={account.metrics.health} />}
            {account.archived && <Badge tone="neutral" variant="solid">Archived</Badge>}
          </>
        }
        kpis={heroKpis}
        actions={
          <div className="aw-hero-qab">
            <QuickActionBar
              contacts={toActionContacts(derived.contacts)}
              attachTo={{ kind: 'account', id: account.id, label: account.name }}
              contextLabel={account.name}
              tel={tel}
            />
            <button
              className="aw-archive-link"
              onClick={() => setEditOpen(true)}
              title="Edit this account's details — type, status, contact info, HQ address, description"
            >
              Edit account
            </button>
            {derived.contacts.length > 0 && <button
              className="aw-archive-link"
              onClick={() => setEmailSyncOpen(true)}
              title="Scan your own mailbox for threads with this account's contacts and add AI summaries to the History tab"
            >
              Sync my email history
            </button>}
            {canManage && <button
              className="aw-archive-link"
              onClick={() => {
                const verb = account.archived ? 'Unarchive' : 'Archive'
                if (!confirm(`${verb} ${account.name}?\n\n${account.archived
                  ? 'It returns to the regular account views.'
                  : 'It moves to the Archived tab (hidden from default views and portfolio stats). Nothing is deleted — unarchive any time.'}`)) return
                patchStore('accounts', account.id, { archived: !account.archived })
              }}
            >
              {account.archived ? 'Unarchive account' : 'Archive account'}
            </button>}
          </div>
        }
      />

      <SyncEmailModal
        open={emailSyncOpen}
        onClose={() => setEmailSyncOpen(false)}
        account={account}
        contacts={derived.contacts}
      />

      <AccountForm open={editOpen} onClose={() => setEditOpen(false)} account={account} />

      <div className="aw-tabs-wrap">
        <Tabs tabs={tabs} active={activeTab} onChange={goTab} />
      </div>

      <div className="aw-tab-body">
        {activeTab === 'overview' && <OverviewTab account={account} d={derived} />}
        {activeTab === 'insights' && <InsightsTab account={account} d={derived} data={data} />}
        {activeTab === 'contacts' && <ContactsTab account={account} d={derived} />}
        {activeTab === 'properties' && <PropertiesTab account={account} d={derived} />}
        {activeTab === 'vital-factors' && <VitalFactorsTab d={derived} />}
        {activeTab === 'financials' && <FinancialsTab account={account} d={derived} data={data} />}
        {activeTab === 'deals' && <DealsTab account={account} d={derived} data={data} />}
        {activeTab === 'diligence' && (
          <GenericEmptyTab
            icon={<Building2 size={36} />}
            title="No diligence reports loaded"
            hint="Acquisition and onboarding due-diligence reports — physical condition findings, CapEx flags, and photo logs — surface here once a deal enters DD. None are loaded for this account in the prototype."
          />
        )}
        {activeTab === 'scheduled' && <ScheduledTab d={derived} />}
        {activeTab === 'action-items' && <ActionItemsTab account={account} d={derived} />}
        {activeTab === 'work-orders' && <WorkOrdersTab account={account} d={derived} />}
        {activeTab === 'projects' && <ProjectsTab d={derived} />}
        {activeTab === 'history' && <HistoryTab d={derived} />}
        {activeTab === 'files' && (
          <GenericEmptyTab
            icon={<Building2 size={36} />}
            title="File library pending integration"
            hint="Reports, agreements, and correspondence sync here from SharePoint and OneDrive. Document integration is pending in the prototype."
          />
        )}
      </div>
    </div>
  )
}
